import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getValidAccessTokenFromStorage, redirectToShellHome } from "@weco/suite-client";

import { officeService } from "../api";
import { useOfficeEntered } from "../lib/officeSession";
import type { MapLayout, PresenceUser } from "../api/types";
import {
  OfficeRealtimeClient,
  type ConnectionStatus,
  type ServerFrame,
} from "./OfficeRealtimeClient";

export function buildWsUrl(): string {
  const envUrl = import.meta.env.VITE_OFFICE_WS_URL;
  if (typeof envUrl === "string" && envUrl.trim()) return envUrl.trim();
  const proto = window.location.protocol === "https:" ? "wss" : "ws";
  // Dev: the office backend is on :8096 and the Vite proxy forwards HTTP only,
  // not WebSockets — so the handshake has to target the backend directly.
  // Matching on "localhost" alone is not enough: the shell is just as often
  // opened on 127.0.0.1, and that spelling would fall through to the shell's
  // own port, where nothing answers the upgrade and the floor silently stays
  // offline. Prod: the shell host serves /ws/.
  const { hostname, host } = window.location;
  if (hostname === "localhost" || hostname === "127.0.0.1" || hostname === "[::1]") {
    return `${proto}://${hostname}:8096/ws/v1.0/office/`;
  }
  return `${proto}://${host}/ws/v1.0/office/`;
}

export interface OfficeRealtimeValue {
  client: OfficeRealtimeClient;
  status: ConnectionStatus;
  /** Everyone currently on the floor, keyed by user id. */
  people: Record<string, PresenceUser>;
  layout: MapLayout | null;
  /** Emitted by the server when a private room refuses entry. */
  lockedRoom: LockedRoom | null;
  clearLockedRoom: () => void;
  subscribe: (listener: (frame: ServerFrame) => void) => () => void;
}

export interface LockedRoom {
  zone_id?: string;
  zone_name?: string;
  reason: string;
  occupants?: number;
  return_to: { x: number; y: number };
}

const Ctx = createContext<OfficeRealtimeValue | null>(null);

export function OfficeRealtimeProvider({ children }: { children: ReactNode }) {
  // The provider is mounted for the whole signed-in shell so presence outlives
  // the module, but it only opens the socket once this tab has actually opened
  // HomeWork. Somebody who never goes there is not on the floor.
  const entered = useOfficeEntered();
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [people, setPeople] = useState<Record<string, PresenceUser>>({});
  const [layout, setLayout] = useState<MapLayout | null>(null);
  const [lockedRoom, setLockedRoom] = useState<LockedRoom | null>(null);
  const clientRef = useRef<OfficeRealtimeClient | null>(null);
  const queryClient = useQueryClient();
  // The frame handler is registered once, so it cannot read `layout` from
  // state without closing over the value it had on mount.
  const layoutRef = useRef<MapLayout | null>(null);
  layoutRef.current = layout;

  if (clientRef.current === null) {
    clientRef.current = new OfficeRealtimeClient({
      url: buildWsUrl(),
      getToken: () => getValidAccessTokenFromStorage(),
      onAuthFailure: () => redirectToShellHome(),
    });
  }

  useEffect(() => {
    if (!entered) return;
    const client = clientRef.current!;
    const unsubStatus = client.onStatusChange(setStatus);

    /**
     * Pull the floor plan again and hand it to everything that draws from it.
     *
     * The socket only sends a layout inside `presence.snapshot`, which arrives
     * once on connect — so a live edit has to be fetched. Both copies have to
     * be replaced: `FloorRoute` prefers the socket's layout over the REST one,
     * so refreshing only the query cache would leave the canvas drawing the
     * plan as it was when the user connected.
     */
    const refreshLayout = async () => {
      try {
        setLayout(await officeService.getMap());
      } catch {
        // A failed refresh is not worth a toast: the floor on screen is stale,
        // not broken, and the next snapshot or navigation corrects it.
      }
      void queryClient.refetchQueries({ queryKey: ["office", "map"] });
    };

    const unsubFrames = client.subscribe((frame) => {
      switch (frame.type) {
        case "presence.snapshot": {
          const payload = frame.payload as unknown as {
            users: PresenceUser[];
            layout: MapLayout;
          };
          setLayout(payload.layout);
          setPeople(
            Object.fromEntries((payload.users ?? []).map((u) => [u.user_id, u])),
          );
          // Connecting is itself a presence change — the server has just put
          // this user on the floor and opened a work session. Anything already
          // fetched over REST (the check-in boards, the presence list, /me/)
          // was read before that happened and now shows them offline.
          //
          // The refresh hangs off the snapshot rather than the connection
          // status because the snapshot is the first thing the server sends
          // *after* processing the connect, and it is dispatched a tick later
          // than the status change — long enough that these queries have
          // settled and a refetch is a real request rather than being deduped
          // into the one already in flight, which is what returns the stale
          // pre-connect answer.
          for (const key of [
            ["office", "me"],
            ["office", "timer"],
            ["office", "presence"],
            ["office", "reports"],
            ["office", "floors"],
          ]) {
            void queryClient.refetchQueries({ queryKey: key });
          }
          break;
        }
        case "presence.update": {
          const user = (frame.payload as { user: PresenceUser }).user;
          setPeople((prev) => ({ ...prev, [user.user_id]: user }));
          break;
        }
        case "presence.offline": {
          const userId = (frame.payload as { user_id: string }).user_id;
          setPeople((prev) => {
            const next = { ...prev };
            delete next[userId];
            return next;
          });
          break;
        }
        case "map.positions": {
          // Positions arrive far more often than anything else, so they patch
          // the existing card rather than replacing it — a move must not blank
          // out somebody's status or pinned ticket between presence updates.
          const updates = (frame.payload as {
            positions: { user_id: string; x: number; y: number; facing: string }[];
          }).positions;
          setPeople((prev) => {
            const next = { ...prev };
            for (const update of updates) {
              const existing = next[update.user_id];
              if (!existing) continue;
              next[update.user_id] = {
                ...existing,
                position: {
                  x: update.x,
                  y: update.y,
                  facing: update.facing as PresenceUser["position"]["facing"],
                },
              };
            }
            return next;
          });
          break;
        }
        case "map.layout": {
          // An admin saved a floor plan. Redraw without anybody reloading —
          // rooms an admin moved are rooms people are standing in.
          const payload = frame.payload as { map_id?: string | null; revision?: number | null };
          const current = layoutRef.current;
          // A different floor's edit still changes the lift (a rename, a new
          // default), but must not repaint the floor under somebody's feet.
          void queryClient.refetchQueries({ queryKey: ["office", "floors"] });
          if (payload.map_id && current && payload.map_id !== current.id) break;
          // Frames can arrive out of order; never walk back onto an older plan.
          if (
            typeof payload.revision === "number" &&
            current &&
            payload.revision <= current.revision
          ) {
            break;
          }
          void refreshLayout();
          break;
        }
        case "map.floors": {
          // A floor was added or removed. The lift is stale for everyone, and
          // anyone who was standing on a deleted floor has been moved by the
          // server — so their own layout needs re-reading too.
          void queryClient.refetchQueries({ queryKey: ["office", "floors"] });
          void refreshLayout();
          break;
        }
        case "room.locked": {
          setLockedRoom(frame.payload as unknown as LockedRoom);
          break;
        }
        default:
          break;
      }
    });

    void client.connect();
    return () => {
      unsubStatus();
      unsubFrames();
      client.disconnect();
    };
  }, [queryClient, entered]);

  const subscribe = useCallback(
    (listener: (frame: ServerFrame) => void) => clientRef.current!.subscribe(listener),
    [],
  );
  const clearLockedRoom = useCallback(() => setLockedRoom(null), []);

  const value = useMemo<OfficeRealtimeValue>(
    () => ({
      client: clientRef.current!,
      status,
      people,
      layout,
      lockedRoom,
      clearLockedRoom,
      subscribe,
    }),
    [status, people, layout, lockedRoom, clearLockedRoom, subscribe],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useOfficeRealtime(): OfficeRealtimeValue {
  const value = useContext(Ctx);
  if (!value) {
    throw new Error("useOfficeRealtime must be used inside OfficeRealtimeProvider");
  }
  return value;
}
